import { MessageSquareOff, RotateCcw, PhoneOff } from "lucide-react";
import {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardContent,
  CardDecoration,
  CardBadge,
} from "@/components/modern-ui/card";

const painPoints = [
  {
    icon: MessageSquareOff,
    title: "Late replies",
    desc: "A lead messages while you're in deep work or on a call. By the time you answer, they've already moved on.",
    badge: "Response gap",
    badgeClass:
      "border-violet-500/25 bg-violet-500/10 text-violet-200/90",
    decorationClass: "bg-violet-500/[0.14]",
    iconWrapClass: "border-violet-500/20 bg-violet-500/[0.08] text-violet-200",
  },
  {
    icon: RotateCcw,
    title: "Stuck in the thread",
    desc: "You trade three, four, five messages to learn budget and timing, and still nothing lands on the calendar.",
    badge: "Qualification drag",
    badgeClass:
      "border-fuchsia-500/25 bg-fuchsia-500/10 text-fuchsia-200/90",
    decorationClass: "bg-fuchsia-500/[0.12]",
    iconWrapClass: "border-fuchsia-500/20 bg-fuchsia-500/[0.08] text-fuchsia-200",
  },
  {
    icon: PhoneOff,
    title: "Bad-fit calls",
    desc: "Too many slots go to leads that were never a match. You still pay prep, context switching, and the time on the call.",
    badge: "Calendar cost",
    badgeClass:
      "border-violet-400/25 bg-violet-400/[0.08] text-violet-100/85",
    decorationClass: "bg-violet-400/[0.1]",
    iconWrapClass: "border-violet-400/20 bg-violet-400/[0.07] text-violet-100/90",
  },
];

export default function ProblemSection() {
  return (
    <section data-testid="problem-section" className="relative py-14 sm:py-[4.25rem]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="reveal text-center mb-6 sm:mb-7">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-violet-300/55 mb-3">
            The problem
          </p>
          <h2
            data-testid="problem-headline"
            className="text-3xl sm:text-4xl tracking-tight font-semibold max-w-2xl mx-auto leading-tight"
          >
            Most inbound never becomes a booked call.
            <br />
            <span className="text-base sm:text-lg text-violet-200/45 font-normal block mt-3 max-w-xl mx-auto leading-snug">
              Slow replies, scattered threads, and time lost to the wrong meetings.
            </span>
          </h2>
        </div>

        <p
          className="reveal text-center text-sm sm:text-base text-violet-200/50 max-w-md mx-auto mb-10 sm:mb-11"
          data-testid="problem-bridge"
        >
          Sound familiar? Here&apos;s how it usually shows up.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 reveal stagger-children">
          {painPoints.map((p) => {
            const Icon = p.icon;
            return (
              <Card
                key={p.title}
                data-testid={`problem-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex h-full min-h-[12.5rem] flex-col overflow-visible border-violet-500/12 bg-gradient-to-br from-[#1a1726] via-[#13111c] to-[#0e0c14] shadow-md shadow-black/20 ring-1 ring-white/[0.03] transition-transform duration-300 hover:-translate-y-1 hover:border-violet-500/20 hover:shadow-lg hover:shadow-violet-950/25"
              >
                <CardDecoration className={p.decorationClass} />
                <CardHeader className="relative z-[1] pb-3">
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border [&_svg]:h-[1.35rem] [&_svg]:w-[1.35rem] ${p.iconWrapClass}`}
                      aria-hidden
                    >
                      <Icon />
                    </div>
                    <CardTitle className="min-w-0 text-[1.0625rem] font-semibold tracking-tight text-zinc-100">
                      {p.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="relative z-[1] flex-1">
                  <p className="text-[0.875rem] leading-[1.65] text-zinc-400 pb-1">
                    {p.desc}
                  </p>
                </CardContent>
                <CardFooter className="relative z-[1] mt-auto">
                  <CardBadge className={p.badgeClass}>{p.badge}</CardBadge>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
